import { nextTick, ref, watch } from 'vue'

import { applyBackgroundBlurForLocalPublish } from '@/features/background-blur'

import { useStoreMediaDevices } from '@/entities/media-devices'
import {
  isBackgroundBlurEnabled,
  PreferenceKey,
  usePreferencesStore,
} from '@/entities/preference'

const attachStreamToVideo = async (
  video: HTMLVideoElement,
  stream: MediaStream,
) => {
  video.muted = true
  video.defaultMuted = true
  video.autoplay = true
  video.playsInline = true
  video.setAttribute('playsinline', 'true')
  video.srcObject = stream
  try {
    await video.play()
  } catch {
    // Autoplay can reject if the element was detached mid-start; srcObject is enough.
  }
}

export const useLocalWebcamVideo = () => {
  const webcamElement = ref<HTMLVideoElement>()
  const { closeStream, openWebcam } = useStoreMediaDevices()
  const currentStream = ref<MediaStream | null>(null)

  watch(webcamElement, (el) => {
    if (el && currentStream.value && el.srcObject !== currentStream.value) {
      void attachStreamToVideo(el, currentStream.value)
    }
  })

  const closeWebcam = () => {
    if (webcamElement.value) {
      webcamElement.value.srcObject = null
    }

    if (currentStream.value) {
      closeStream(currentStream.value)
      currentStream.value = null
    }
  }

  const startLocalStream = async () => {
    closeWebcam()
    const rawStream = await openWebcam()
    currentStream.value = rawStream
    await nextTick()
    if (webcamElement.value) {
      await attachStreamToVideo(webcamElement.value, rawStream)
    }

    const preferencesStore = usePreferencesStore()
    if (!isBackgroundBlurEnabled(preferencesStore.getPreference(PreferenceKey.BACKGROUND_BLUR_ENABLED))) {
      return rawStream
    }

    if (currentStream.value !== rawStream) {
      return currentStream.value ?? undefined
    }

    try {
      const processed = await applyBackgroundBlurForLocalPublish(rawStream)
      if (currentStream.value !== rawStream) {
        if (processed.id !== rawStream.id) {
          closeStream(processed)
        }
        return currentStream.value ?? undefined
      }
      if (processed.id === rawStream.id) {
        return rawStream
      }
      currentStream.value = processed
      if (webcamElement.value) {
        await attachStreamToVideo(webcamElement.value, processed)
      }
      return processed
    } catch (error) {
      console.warn('[background-blur] preview upgrade failed, keeping raw camera', error)
      return rawStream
    }
  }

  return {
    webcamElement,
    startLocalStream,
    closeWebcam,
  }
}
